import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, Settings } from 'lucide-react';
import { User } from '../services/auth';
import { useResponsive } from '@turn-connect/hooks';
import { useLayoutWidth } from '../hooks/useLayoutWidth';
import { useThemeChoice } from '../theme/ThemeProvider';
import { THEME_PICKER_ENABLED, type ThemeChoice } from '../theme/themeController';
import { themePreferencesService } from '../services/themePreferences';

const THEME_OPTIONS: { value: ThemeChoice; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'Match system', Icon: Monitor },
];

interface UserProfileDropdownProps {
  user: User;
  permissions: string[];
  onLogout: () => void;
  onEditProfile: () => void;
  onAdminDashboard?: () => void;
  /**
   * Opens the settings of the current scope (GlobalHeader decides which, and
   * leaves this out when the viewer may open neither).
   */
  onOpenSettings?: () => void;
  /** Which settings it opens, shown beside the item: "Account" or "Location". */
  settingsScopeLabel?: string;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  user,
  permissions,
  onLogout,
  onEditProfile,
  onAdminDashboard,
  onOpenSettings,
  settingsScopeLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { mode: layoutWidthMode, setMode: setLayoutWidthMode } = useLayoutWidth();
  // On a phone the header has no room for the chevron; the avatar alone opens the menu.
  const { isMobile } = useResponsive();
  const isFullWidth = layoutWidthMode === 'full';
  const { choice: themeChoice, setChoice: setThemeChoice } = useThemeChoice();
  const pickTheme = (next: ThemeChoice) => setThemeChoice(next, (c) => void themePreferencesService.updateTheme(c));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  // Ensure permissions is always an array
  const userPermissions = permissions || [];

  // Check if user has admin permissions
  const isAdmin =
    userPermissions.some(
      (permission) =>
        permission.includes('admin') ||
        permission.includes('system:') ||
        permission.includes('locations:') ||
        permission.includes('accounts:')
    ) ||
    // Also check for system administrator users
    user.email?.includes('sysadmin') ||
    user.email?.includes('admin@') ||
    // Force admin for demo account
    user.email === 'sysadmin@turnconnect.demo';

  const displayName = user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.name || user.email || 'User';

  return (
    <div className="user-profile-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="user-menu-button"
        aria-label="Account menu"
        aria-expanded={isOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: isMobile ? '0.375rem' : '0.375rem 0.5rem',
          backgroundColor: 'transparent',
          border: '1px solid var(--tc-border-light, #333333)',
          borderRadius: '0.5rem',
          color: 'var(--tc-text-primary, #FFFFFF)',
          cursor: 'pointer',
          fontSize: '0.8125rem',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#2A2A2A';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {/* Avatar only - show initials */}
        {
          <div
            style={{
              width: '1.75rem',
              height: '1.75rem',
              borderRadius: '50%',
              backgroundColor: 'var(--tc-interactive-secondary, #6B7280)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#FFFFFF',
            }}
          >
            {displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : 'U'}
          </div>
        }

        {/* Chevron */}
        {!isMobile && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: '0',
            marginTop: '0.5rem',
            minWidth: '18.75rem',
            backgroundColor: 'var(--tc-background-tertiary, #1A1A1A)',
            border: '1px solid var(--tc-border-light, #333333)',
            borderRadius: '0.75rem',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* User Info Header */}
          <div
            style={{
              padding: '1rem',
              borderBottom: '1px solid var(--tc-border-light, #333333)',
            }}
          >
            <div style={{ fontWeight: '600', fontSize: '0.8125rem', marginBottom: '0.25rem' }}>{displayName}</div>
            <div style={{ color: 'var(--tc-text-tertiary, #999999)', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
              {typeof user.email === 'string' ? user.email : ''}
            </div>
            <div
              style={{
                display: 'inline-block',
                padding: '0.125rem 0.5rem',
                backgroundColor: 'var(--tc-status-success, #4CAF50)',
                borderRadius: '0.25rem',
                fontSize: '0.625rem',
                fontWeight: '500',
                textTransform: 'uppercase',
                color: 'var(--tc-text-onAccent, #FFFFFF)',
              }}
            >
              {typeof user.status === 'string' && user.status ? user.status : 'active'}
            </div>
            {isAdmin && (
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '0.5rem',
                  padding: '0.125rem 0.5rem',
                  backgroundColor: 'var(--tc-brand-accent, #FF9500)',
                  borderRadius: '0.25rem',
                  fontSize: '0.625rem',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  color: 'var(--tc-text-primary, #FFFFFF)',
                }}
              >
                Admin
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div style={{ padding: '0.5rem 0' }}>
            {/* Edit Profile */}
            <button
              onClick={() => {
                onEditProfile();
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'var(--tc-text-primary, #FFFFFF)',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                transition: 'background-color 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#2A2A2A';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 8C10.2091 8 12 6.20914 12 4C12 1.79086 10.2091 0 8 0C5.79086 0 4 1.79086 4 4C4 6.20914 5.79086 8 8 8Z"
                  fill="#CCCCCC"
                />
                <path d="M8 10C3.58172 10 0 13.5817 0 18H16C16 13.5817 12.4183 10 8 10Z" fill="#CCCCCC" />
              </svg>
              Edit Profile
            </button>

            {/* Settings: the current account's (or, without account access,
              the current location's) settings dialog. */}
            {onOpenSettings && (
              <button
                type="button"
                data-testid="user-menu-settings"
                aria-label={settingsScopeLabel ? `Settings (${settingsScopeLabel})` : 'Settings'}
                title={settingsScopeLabel ? `${settingsScopeLabel} settings` : undefined}
                onClick={() => {
                  setIsOpen(false);
                  onOpenSettings();
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'var(--tc-text-primary, #FFFFFF)',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#2A2A2A';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Settings size={16} style={{ color: '#CCCCCC', flexShrink: 0 }} aria-hidden="true" />
                <span style={{ flex: 1 }}>Settings</span>
                {settingsScopeLabel && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--tc-text-tertiary, #999999)' }}>{settingsScopeLabel}</span>
                )}
              </button>
            )}

            {/* Full-width layout toggle */}
            <button
              role="menuitemcheckbox"
              aria-checked={isFullWidth}
              onClick={() => setLayoutWidthMode(isFullWidth ? 'comfortable' : 'full')}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'var(--tc-text-primary, #FFFFFF)',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                transition: 'background-color 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#2A2A2A';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2 5V2H5M11 2H14V5M14 11V14H11M5 14H2V11"
                  stroke="#CCCCCC"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={{ flex: 1 }}>Full-width layout</span>
              {/* Compact switch reflecting the mode */}
              <span
                aria-hidden="true"
                style={{
                  width: '2rem',
                  height: '1.125rem',
                  borderRadius: '0.5625rem',
                  backgroundColor: isFullWidth ? 'var(--tc-brand-primary, #E3522E)' : 'var(--tc-background-elevated, #333333)',
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'background-color 0.2s ease',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '0.125rem',
                    left: isFullWidth ? 'calc(100% - 1rem)' : '0.125rem',
                    width: '0.875rem',
                    height: '0.875rem',
                    borderRadius: '50%',
                    backgroundColor: 'var(--tc-background-default, #FFFFFF)',
                    transition: 'left 0.2s ease',
                  }}
                />
              </span>
            </button>

            {/* Appearance — compact theme selector */}
            {THEME_PICKER_ENABLED && (
              <div style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Sun size={16} style={{ color: 'var(--tc-text-tertiary, #999999)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.8125rem', color: 'var(--tc-text-primary, #FFFFFF)' }}>Appearance</span>
                <div
                  role="radiogroup"
                  aria-label="Appearance"
                  style={{
                    display: 'flex',
                    border: '1px solid var(--tc-border-default, #2A2A1F)',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {THEME_OPTIONS.map((opt) => {
                    const selected = themeChoice === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        title={opt.label}
                        aria-label={opt.label}
                        onClick={() => pickTheme(opt.value)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '2rem',
                          height: '1.75rem',
                          border: 'none',
                          cursor: 'pointer',
                          backgroundColor: selected ? 'var(--tc-brand-fill, #C84826)' : 'transparent',
                          color: selected ? 'var(--tc-text-onAccent, #FFFFFF)' : 'var(--tc-text-tertiary, #999999)',
                          transition: 'background-color 0.15s ease',
                        }}
                      >
                        <opt.Icon size={14} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ height: '1px', backgroundColor: 'var(--tc-background-elevated, #333333)', margin: '0.5rem 0' }} />

            {/* Logout */}
            <button
              onClick={() => {
                onLogout();
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'var(--tc-brand-primary, #DA562C)',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                transition: 'background-color 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#2A1A1A';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 2H14V14H6M2 8H10M8 6L10 8L8 10"
                  stroke="#FF6B6B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
